import { IonRouterLink } from '@ionic/react';
import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import InitialConsultSocialForm from '@components/forms/InitialConsultSocialForm';
import PersonalizeForm from '@components/forms/PersonalizeForm';
import InitialConsultQuestion from '@components/InitialConsultQuestion';
import Modal from '@components/Modal';
import PMEQuestion from '@components/PMEQuestion';
import styles from '@components/profile/Profile.module.css';
import Icon from '@components/text/Icon';
import Heading2 from '@components/typography/Heading2';
import useOpener from '@hooks/useOpener';
import {
  CareProfileActionableFieldEnum,
  CareProfileEmotionalConcernEnum,
  GetJourneyWithCareProfileQuery
} from '@typing/Generated';
import { InitialConsultFields, PersonalizeExperienceFields } from '@typing/Models';
type Props = {
  journey: GetJourneyWithCareProfileQuery['journey'];
};

const Social = ({ journey }: Props) => {
  const opener = useOpener();
  const intl = useIntl();

  const personalizeSocialForm = useOpener();
  const initialConsultOpener = useOpener();

  const [editingQuestion, setEditingQuestion] = useState<PersonalizeExperienceFields[]>([]);
  const [editingInitialConsult, setEditingInitialConsult] = useState<InitialConsultFields[] | undefined>(undefined);

  const handlePMEQuestionChange = useCallback(
    (field: PersonalizeExperienceFields) => () => {
      setEditingQuestion([field]);
      personalizeSocialForm.open();
    },
    [personalizeSocialForm]
  );

  const handleInitialConsultQuestionChange = useCallback(
    (field: InitialConsultFields) => () => {
      setEditingInitialConsult([field]);
      initialConsultOpener.open();
    },
    [initialConsultOpener]
  );

  return (
    <div>
      <IonRouterLink onClick={opener.toggle}>
        <div className={styles.section}>
          <Heading2>
            <FormattedMessage id="pages.client.careProfile.sections.social" />
          </Heading2>
          <Icon
            ariaLabelKey="accessibility.ariaLabels.toggle"
            className={styles.toggleOpener}
            icon={opener.isOpen ? 'chevronUp' : 'chevronDown'}
          />
        </div>
      </IonRouterLink>
      <ExpandableSection isOpen={opener.isOpen}>
        <PMEQuestion
          field={CareProfileActionableFieldEnum.sense_of_fulfillment}
          handleEdit={handlePMEQuestionChange}
          selections={
            journey.careProfile.senseOfFulfillment
              ? [intl.formatMessage({ id: `models.careProfile.range.${journey.careProfile.senseOfFulfillment}` })]
              : []
          }
        />
        <PMEQuestion
          field={CareProfileActionableFieldEnum.social_satisfaction}
          handleEdit={handlePMEQuestionChange}
          selections={
            journey.careProfile.socialSatisfaction
              ? [intl.formatMessage({ id: `models.careProfile.range.${journey.careProfile.socialSatisfaction}` })]
              : []
          }
        />
        <InitialConsultQuestion
          field={InitialConsultFields.EMOTIONAL_CONCERNS}
          handleEdit={handleInitialConsultQuestionChange}
          selections={
            journey.careProfile.emotionalConcerns?.emotionalConcernsOptions?.map(option => {
              if (option === CareProfileEmotionalConcernEnum.other && journey.careProfile.emotionalConcerns?.other) {
                return journey.careProfile.emotionalConcerns.other;
              }
              return intl.formatMessage({
                id: `models.initialConsultReport.questions.emotional_concerns.options.${option}.content`
              });
            }) ?? []
          }
          titleKey="models.initialConsultReport.questions.emotional_concerns.content"
        />
      </ExpandableSection>
      <Modal opener={personalizeSocialForm} title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}>
        {() => (
          <PersonalizeForm
            careProfile={journey.careProfile}
            clientRole={null}
            onCancel={personalizeSocialForm.close}
            onSuccess={personalizeSocialForm.close}
            questions={editingQuestion}
          />
        )}
      </Modal>
      <Modal opener={initialConsultOpener} title={intl.formatMessage({ id: 'models.careProfile.editCareProfile' })}>
        {() => (
          <InitialConsultSocialForm
            careProfile={journey.careProfile}
            consultId={journey.initialConsultReport.consult?.id ?? null}
            initialConsultReportId={journey.initialConsultReport.id}
            onCancel={initialConsultOpener.close}
            onSuccess={initialConsultOpener.close}
            questions={editingInitialConsult}
          />
        )}
      </Modal>
    </div>
  );
};

export default Social;
